import type { PrintDocument } from './printRegions';
import type { PrintSheetHtml, PrintSheetLayout } from '../print/templates';
import type { ShipLineInput } from './shipment';
import { dropShipHoldProblem, type DropShipHold } from './dropShip';

/**
 * The Package (piece 22; decision 1730): a numbered box packed against a
 * Released Sales Order, between the order and the Shipment that carries it.
 * Packing four of ten is a package line of four, never a change to the order.
 * The Shipment raised from packages (decision 1731) adds their lines up by
 * order line and confirms through the one confirmation path. Nothing here
 * touches a table.
 */
export const PACKAGE_STATUSES = ['NotShipped', 'Shipped'] as const;
export type PackageStatusValue = (typeof PACKAGE_STATUSES)[number];

/** 🔒 The seeded package types (decision 1730), in their order; the migration writes the same five. */
export const PACKAGE_TYPE_SEEDS = ['Box', 'Envelope', 'Pallet', 'Crate', 'Tube'] as const;

/** The units the window offers, by abbreviation: a weight reads in the Mass family, a dimension in the Length family. */
export const PACKAGE_WEIGHT_UNITS = ['lb', 'oz', 'kg', 'g'] as const;
export const PACKAGE_DIMENSION_UNITS = ['in', 'ft', 'cm', 'm'] as const;

/** One order line as the package window reads it. */
export interface PackSourceLine {
  id: string;
  itemId: string | null;
  /** 🔨 A Service line ships nothing from a shelf, so it is never packed. */
  isService: boolean;
  sku: string | null;
  description: string;
  unitId: string;
  unitName: string;
  /** The ordered quantity. */
  quantity: number;
  /** The order line's counter: shipped, or fitted on a Work Order. */
  quantityConsumed: number;
  /** What every other package of the order holds against the line, shipped or not. */
  packedElsewhere: number;
  /** What the order's Shipped packages, this one aside, hold against the line: the part of the counter a package explains. */
  shippedInPackages: number;
  /** 🔒 The part a drop-ship order not yet confirmed ships from the vendor (decision 1930); absent reads as none. */
  dropShipHold?: DropShipHold | null;
}

/**
 * 🔨 What the line has left to pack: the ordered quantity, less what every
 * other package holds, less what left the shelf against it outside any
 * package (decision 1730).
 */
export function leftToPack(line: PackSourceLine): number {
  const consumedOutsidePackages = Math.max(0, line.quantityConsumed - line.shippedInPackages);
  return Math.max(0, line.quantity - line.packedElsewhere - consumedOutsidePackages - (line.dropShipHold?.quantity ?? 0));
}

export interface PackLineInput {
  sourceLineId: string;
  quantity: number;
}

/** A package carries at least one line, each on a goods line of its order, above zero and within what is left to pack. */
export function packageLinesProblem(sourceLines: readonly PackSourceLine[], lines: readonly PackLineInput[]): string | null {
  const packed = lines.filter((line) => line.quantity !== 0);
  if (packed.length === 0) return 'a package carries at least one line; enter the quantity packed on the lines going into the box';
  const seen = new Set<string>();
  for (const line of packed) {
    const source = sourceLines.find((s) => s.id === line.sourceLineId);
    if (!source) return 'a package line names a line of the order it packs; reload the order and pack against the lines it shows';
    if (seen.has(source.id)) return 'a package names each order line once; add the quantities together on one line';
    seen.add(source.id);
    const name = source.sku ?? source.description;
    if (!source.itemId || source.isService) return `${name} is not a part off a shelf and is not packed`;
    if (!Number.isFinite(line.quantity) || line.quantity < 0) return 'a packed quantity is above zero';
    const left = leftToPack(source);
    if (line.quantity > left + 1e-9) {
      // 🔨 The vendor's part is refused by the purchase order's number, so nobody packs it twice (decision 1930).
      if (source.dropShipHold && source.dropShipHold.quantity > 0) return dropShipHoldProblem(name, source.dropShipHold, left, line.quantity, 'pack');
      return `${name} has ${left} left to pack and this package holds ${line.quantity}`;
    }
  }
  return null;
}

/** The lines a new package opens offering: every goods line with something left to pack, at what is left. */
export function packableLines(sourceLines: readonly PackSourceLine[]): PackLineInput[] {
  return sourceLines.filter((s) => s.itemId && !s.isService && leftToPack(s) > 0).map((s) => ({ sourceLineId: s.id, quantity: leftToPack(s) }));
}

/** 🔨 A Shipped package is never edited: the slip in the box is what went out. */
export function packageEditProblem(status: PackageStatusValue): string | null {
  return status === 'Shipped' ? 'a shipped package is not edited; the slip in the box is what went out' : null;
}

/** 🔨 A Shipped package is never deleted: the slip in the box is what went out (decision 2532). */
export function packageDeleteProblem(status: PackageStatusValue): string | null {
  return status === 'Shipped' ? 'a shipped package is not deleted; the slip in the box is what went out' : null;
}

/** The packages a shipment is raised from: at least one, each Not Shipped and each of the order named. */
export function shipPackagesProblem(salesOrderId: string, packages: readonly { id: string; packageNumber: string; status: PackageStatusValue; salesOrderId: string }[], askedFor: readonly string[]): string | null {
  if (askedFor.length === 0) return 'to create a shipment a package is needed; pick one or more packages that have not shipped';
  for (const id of askedFor) {
    const found = packages.find((p) => p.id === id);
    if (!found || found.salesOrderId !== salesOrderId) return 'a package on the shipment belongs to the order it ships against; reload the order and pick its packages';
    if (found.status !== 'NotShipped') return `${found.packageNumber} has already shipped`;
  }
  return null;
}

/** 🔒 The shipment's lines: the packages' lines added up by order line, in the order they first appear (decision 1731). */
export function packageLinesSummed(lines: readonly { sourceLineId: string; quantity: number }[]): ShipLineInput[] {
  const totals = new Map<string, number>();
  for (const line of lines) totals.set(line.sourceLineId, (totals.get(line.sourceLineId) ?? 0) + line.quantity);
  return [...totals].map(([sourceLineId, quantity]) => ({ sourceLineId, quantity }));
}

/** The total quantity a package holds, the slip's Total Qty box. */
export function packageTotalQuantity(lines: readonly { quantity: number }[]): number {
  return Math.round(lines.reduce((sum, line) => sum + line.quantity, 0) * 10000) / 10000;
}

/** What the notify tick mails the customer (decision 1731): the shipment, the order, the date and how to follow it. */
export function shipmentNoticeMail(facts: {
  organizationName: string;
  shipmentNumber: string;
  salesOrderNumber: string;
  customerPoNumber: string | null;
  shippedOn: string;
  delivered: boolean;
  carrier: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
}): { subject: string; body: string } {
  const state = facts.delivered ? 'has been delivered' : 'has shipped';
  const reference = facts.customerPoNumber ? `${facts.salesOrderNumber} (your purchase order ${facts.customerPoNumber})` : facts.salesOrderNumber;
  const lines = [
    `Your order ${reference} ${state}.`,
    '',
    `Shipment: ${facts.shipmentNumber}`,
    `Shipped on: ${facts.shippedOn}`,
    ...(facts.carrier ? [`Carrier: ${facts.carrier}`] : []),
    ...(facts.trackingNumber ? [`Tracking number: ${facts.trackingNumber}`] : []),
    ...(facts.trackingUrl ? [`Track it here: ${facts.trackingUrl}`] : []),
  ];
  return { subject: `${facts.organizationName ? `${facts.organizationName}: ` : ''}order ${facts.salesOrderNumber} ${state}`, body: lines.join('\n') };
}

/**
 * The address a tracking link opens; a typed address with no scheme is read as https, and anything else is refused.
 * 🔒 A scheme other than http or https is refused (T1141): `javascript:` and `data:` addresses never reach a customer's mail or link. A host with a port, such as `carrier.example:8080/track`, has no scheme.
 */
export function trackingUrlProblem(url: string | null | undefined): string | null {
  if (!url || url.trim() === '') return null;
  const typed = url.trim();
  const otherScheme = /^[a-z][a-z0-9+.-]*:(?!\d+(?:\/|$))/i.test(typed) && !/^https?:\/\//i.test(typed);
  return !otherScheme && /^(https?:\/\/)?[^\s/$.?#].[^\s]*$/i.test(typed) ? null : 'the tracking address is a web address, such as https://carrier.example/track/123';
}

export function trackingHref(url: string | null): string | null {
  if (!url) return null;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

// ---- The shapes the API and the screen exchange ----

export interface PackageHeaderInput {
  packageDate: string;
  packageTypeId?: string | null;
  netWeight?: number | null;
  grossWeight?: number | null;
  weightUnitId?: string | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  dimensionUnitId?: string | null;
}

export interface NewPackageRequest extends PackageHeaderInput {
  salesOrderId: string;
  lines: PackLineInput[];
}

/** 🔒 `version` is the counter the window opened on (decision 1730; T1141): an edit that meets a newer one is refused, and one that names none is refused too (T3029 S9). */
export type PackageEditRequest = PackageHeaderInput & { lines: PackLineInput[]; version: number };

/** What the package window opens on: the order's lines with what each has left, the types and the two unit lists. */
export interface PackageFormAnswer {
  salesOrderId: string;
  salesOrderNumber: string;
  lines: PackSourceLine[];
  packageTypes: { id: string; label: string }[];
  weightUnits: { id: string; label: string }[];
  dimensionUnits: { id: string; label: string }[];
  defaultWeightUnitId: string | null;
  defaultDimensionUnitId: string | null;
  /** The package being edited, where the window reopens one. */
  current: (PackageHeaderInput & { lines: PackLineInput[]; version: number }) | null;
}

export interface PackageLineAnswer {
  id: string;
  sourceLineId: string;
  sku: string | null;
  itemName: string;
  quantity: number;
  unitName: string;
}

/** One row of the order's Packages block. */
export interface PackageRow {
  id: string;
  packageNumber: string;
  packageDate: string;
  status: PackageStatusValue;
  lineCount: number;
  totalQuantity: number;
  shipmentId: string | null;
  shipmentNumber: string | null;
}

export interface PackageAnswer extends PackageRow {
  salesOrderId: string;
  salesOrderNumber: string;
  orderDate: string;
  companyId: string;
  companyName: string;
  packageTypeLabel: string | null;
  netWeight: number | null;
  grossWeight: number | null;
  weightUnit: string | null;
  length: number | null;
  width: number | null;
  height: number | null;
  dimensionUnit: string | null;
  lines: PackageLineAnswer[];
  /** Whether the order still stands where a package is packed and shipped: Released or Closed. */
  orderReleased: boolean;
  printDocument?: PrintDocument | null;
  printLayout?: PrintSheetLayout | null;
  printSheet?: PrintSheetHtml | null;
  writeRefusal?: string | null;
}

/** What the shipment window opens on: the order's unshipped packages, the carriers already used and the sites stock leaves from. */
export interface ShipFormAnswer {
  salesOrderId: string;
  salesOrderNumber: string;
  packages: { id: string; packageNumber: string; packageDate: string; totalQuantity: number }[];
  carriers: string[];
  originSites: { id: string; label: string }[];
  defaultOriginSiteId: string | null;
}

export interface ShipPackagesRequest {
  salesOrderId: string;
  packageIds: string[];
  shippedOn: string;
  carrier?: string | null;
  trackingNumber: string;
  trackingUrl?: string | null;
  shippingChargesCents?: number | null;
  originSiteId?: string | null;
  /** 🔒 The delivered tick: the shipment moves on to Delivered in the same press. */
  delivered?: boolean;
  /** 🔒 The notify tick: the address the status is mailed to, or nothing where it is not ticked. */
  notifyEmail?: string | null;
}

export interface ShipPackagesAnswer {
  shipmentId: string;
  shipmentNumber: string;
  status: string;
  /** Whether the notice left, where one was asked for; null where none was. */
  notified: 'Sent' | 'Failed' | null;
}

/** One row of the order's Shipments block (decision 1731): the carrier's facts, the charge and whether its slip is drawn yet. */
export interface SalesOrderShipmentRow {
  id: string;
  shipmentNumber: string;
  status: string;
  shippedDate: string | null;
  carrier: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  shippingChargesCents: number | null;
  originLabel: string | null;
  /** 🔒 The drop-ship purchase order the vendor shipped this against, the row's *Drop-shipped · PO-…* (decision 1930); null or absent on our own shipment. */
  dropShipOrderNumber?: string | null;
}
